import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Bot, Plus, ExternalLink, Settings } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import CreateBotWizard from './CreateBotWizard';

interface TenantBotsTabProps {
  tenantId: string;
  tenantIndustry: string | null;
}

interface BotSummary {
  id: string;
  status: string | null;
  environment: string | null;
  mode: string | null;
  created_at: string | null;
  template_id: string | null;
  nuance_pack: any;
}

const getStatusDescription = (status: string): string => {
  const descriptions: Record<string, string> = {
    draft: 'Configuring',
    qa_failed: 'QA Failed',
    ready_for_review: 'Awaiting Review',
    changes_requested: 'Changes Requested',
    approved: 'Ready to Publish',
    live: 'Active',
    paused: 'Paused',
  };
  return descriptions[status] || status;
};

const statusColors: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  qa_failed: 'bg-destructive/10 text-destructive',
  ready_for_review: 'bg-amber-500/10 text-amber-600',
  changes_requested: 'bg-orange-500/10 text-orange-600',
  approved: 'bg-blue-500/10 text-blue-600',
  live: 'bg-green-500/10 text-green-600',
  paused: 'bg-muted text-muted-foreground',
};

const environmentColors: Record<string, string> = {
  preview: 'bg-purple-500/10 text-purple-600',
  production: 'bg-green-500/10 text-green-600',
};

const TenantBotsTab: React.FC<TenantBotsTabProps> = ({ tenantId, tenantIndustry }) => {
  const [bots, setBots] = useState<BotSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const fetchBots = async () => {
    try {
      const { data, error } = await supabase
        .from('bots')
        .select('id, status, environment, mode, created_at, template_id, nuance_pack')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBots(data || []);
    } catch (error) {
      console.error('Error fetching bots:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBots();
  }, [tenantId]);

  const handleBotCreated = () => {
    setCreateDialogOpen(false);
    fetchBots();
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Bots</h3>
          <p className="text-sm text-muted-foreground">
            Configure and manage AI assistants for this tenant
          </p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Create Bot
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Bot</DialogTitle>
            </DialogHeader>
            <CreateBotWizard 
              tenantId={tenantId} 
              tenantIndustry={tenantIndustry}
              onComplete={handleBotCreated} 
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Bots Grid */}
      {bots.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Bot className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No bots configured</h3>
            <p className="text-muted-foreground mt-1 mb-4">
              Create your first bot to get started
            </p>
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Bot
                </Button>
              </DialogTrigger>
            </Dialog>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {bots.map((bot) => {
            const toneModifier = bot.nuance_pack?.tone_modifier || 'neutral';
            return (
              <Card key={bot.id} className="hover:border-primary/50 transition-colors">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Bot className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-base capitalize">
                          {bot.mode?.replace(/_/g, ' ') || 'Client Only'}
                        </CardTitle>
                        <p className="text-xs text-muted-foreground font-mono">
                          {bot.id.slice(0, 8)}...
                        </p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    <Badge 
                      variant="secondary"
                      className={statusColors[bot.status || 'draft']}
                    >
                      {getStatusDescription(bot.status || 'draft')}
                    </Badge>
                    <Badge 
                      variant="outline"
                      className={environmentColors[bot.environment || 'preview']}
                    >
                      {bot.environment || 'preview'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>Tone: <span className="capitalize">{toneModifier}</span></span>
                    <span>•</span>
                    <span>
                      {bot.created_at 
                        ? new Date(bot.created_at).toLocaleDateString() 
                        : 'Unknown'}
                    </span>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Link to={`/admin/bots/${bot.id}`} className="flex-1">
                      <Button variant="outline" size="sm" className="w-full">
                        <Settings className="w-4 h-4 mr-1" />
                        Configure
                      </Button>
                    </Link>
                    <Button variant="ghost" size="sm" disabled title="Preview not available in v1">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TenantBotsTab;
